import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { NeedsSetup } from "@/components/NeedsSetup";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  TrendingUp, FileText, RefreshCw, Loader2, Sparkles, Clock, CheckCircle, 
  AlertCircle, Building2, Users, MapPin, BarChart3, PieChart, Upload,
  IndianRupee, Home, ArrowUpRight, FileImage, Calculator, ScanText, Download, ShieldCheck, Eye, Award
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Feasibility = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPrivileged = user?.is_master_admin || user?.is_sales_admin;
  const [society, setSociety] = useState(null);
  const [publishedFeas, setPublishedFeas] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [activeTab, setActiveTab] = useState("data");
  
  // Property card data
  const [propertyData, setPropertyData] = useState({
    plot_area: "",
    carpet_area: "",
    builtup_area: "",
    fsi_consumed: "",
    fsi_available: "",
    cts_number: "",
    survey_number: ""
  });

  // Document sources for verification
  const [documentSources, setDocumentSources] = useState({
    property_card: null,
    blueprint: null,
    municipal_drawing: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const socRes = await fetch(`${API}/societies`, { credentials: "include" });
      const societies = await socRes.json();
      
      if (societies.length > 0) {
        const soc = societies[0];
        setSociety(soc);
        fetch(`${API}/societies/${soc.society_id}/feasibility-published`, { credentials: "include" })
          .then(r => r.ok ? r.json() : null).then(d => { if (d?.published) setPublishedFeas(d); }).catch(() => {});
        
        // Pre-fill property data from society
        setPropertyData({
          plot_area: soc.plot_area || "",
          carpet_area: soc.carpet_area || "",
          builtup_area: soc.builtup_area || "",
          fsi_consumed: soc.fsi_consumed || "",
          fsi_available: soc.fsi_available || "",
          cts_number: soc.cts_number || "",
          survey_number: soc.survey_number || ""
        });
        
        const reportRes = await fetch(`${API}/societies/${soc.society_id}/feasibility`, { 
          credentials: "include" 
        });
        if (reportRes.ok) {
          const data = await reportRes.json();
          setReport(data);
          if (data.report) {
            parseReportData(data.report);
          }
        }

        const compRes = await fetch(`${API}/societies/${soc.society_id}/compliance-check`, { credentials: "include" });
        if (compRes.ok) {
          const compData = await compRes.json();
          if (compData.report) setCompliance(compData);
        }
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const parseReportData = (reportText) => {
    const data = {
      viability: "High",
      fsiPotential: 3.0,
      additionalArea: 0,
      timeline: "36-48 months",
      riskLevel: "Medium",
    };

    const fsiMatch = reportText.match(/FSI[:\s]+(\d+\.?\d*)/i);
    if (fsiMatch) data.fsiPotential = parseFloat(fsiMatch[1]);

    if (reportText.toLowerCase().includes("highly viable") || reportText.toLowerCase().includes("excellent")) {
      data.viability = "High";
    } else if (reportText.toLowerCase().includes("not viable") || reportText.toLowerCase().includes("not recommended")) {
      data.viability = "Low";
    } else {
      data.viability = "Medium";
    }

    setParsedData(data);
  };

  const handlePropertyDataChange = (field, value) => {
    setPropertyData({ ...propertyData, [field]: value });
  };

  const savePropertyData = async () => {
    if (!society) return;
    
    try {
      const res = await fetch(`${API}/societies/${society.society_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...society,
          plot_area: propertyData.plot_area ? parseFloat(propertyData.plot_area) : null,
          carpet_area: propertyData.carpet_area ? parseFloat(propertyData.carpet_area) : null,
          builtup_area: propertyData.builtup_area ? parseFloat(propertyData.builtup_area) : null,
          fsi_consumed: propertyData.fsi_consumed ? parseFloat(propertyData.fsi_consumed) : null,
          fsi_available: propertyData.fsi_available ? parseFloat(propertyData.fsi_available) : null,
          cts_number: propertyData.cts_number,
          survey_number: propertyData.survey_number
        }),
        credentials: "include"
      });

      if (res.ok) {
        toast.success("Property data saved successfully");
        fetchData();
      } else {
        toast.error("Failed to save property data");
      }
    } catch (error) {
      toast.error("Failed to save property data");
    }
  };

  const extractFromDocument = async (docType) => {
    if (!society) return;
    
    // Find document of this type
    const docsRes = await fetch(`${API}/societies/${society.society_id}/documents`, { credentials: "include" });
    if (!docsRes.ok) return;
    
    const docs = await docsRes.json();
    const relevantDoc = docs.find(d => 
      d.doc_type === docType || 
      d.name.toLowerCase().includes(docType.replace('_', ' '))
    );
    
    if (!relevantDoc) {
      toast.error(`No ${docType.replace('_', ' ')} document found. Please upload one first.`);
      return;
    }
    
    if (!relevantDoc.extracted_text) {
      // Run OCR first
      setExtracting(true);
      toast.info("Extracting text from document...");
      
      try {
        const ocrRes = await fetch(`${API}/societies/${society.society_id}/documents/${relevantDoc.document_id}/ocr`, {
          method: "POST",
          credentials: "include"
        });
        
        if (ocrRes.ok) {
          const result = await ocrRes.json();
          // Now extract specific data
          await extractDataFromText(result.extracted_text, docType);
        }
      } catch (error) {
        toast.error("Failed to extract text");
      } finally {
        setExtracting(false);
      }
    } else {
      await extractDataFromText(relevantDoc.extracted_text, docType);
    }
  };

  const extractDataFromText = async (text, docType) => {
    setExtracting(true);
    try {
      const res = await fetch(`${API}/extract-property-data`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: text,
          doc_type: docType,
          society_id: society.society_id
        }),
        credentials: "include"
      });

      if (res.ok) {
        const extracted = await res.json();
        
        // Update property data with extracted values
        setPropertyData(prev => ({
          ...prev,
          plot_area: extracted.plot_area || prev.plot_area,
          carpet_area: extracted.carpet_area || prev.carpet_area,
          builtup_area: extracted.builtup_area || prev.builtup_area,
          cts_number: extracted.cts_number || prev.cts_number,
          survey_number: extracted.survey_number || prev.survey_number
        }));
        
        setDocumentSources(prev => ({
          ...prev,
          [docType]: { verified: true, data: extracted }
        }));
        
        toast.success(`Data extracted from ${docType.replace('_', ' ')}`);
      }
    } catch (error) {
      toast.error("Failed to extract data");
    } finally {
      setExtracting(false);
    }
  };

  const generateReport = async () => {
    if (!society) return;
    
    // First save the property data
    await savePropertyData();
    
    setGenerating(true);
    toast.info("AI is analyzing documents and calculating feasibility. This may take 60-90 seconds...", { duration: 8000 });
    
    try {
      const res = await fetch(`${API}/societies/${society.society_id}/analyze`, {
        method: "POST",
        credentials: "include"
      });

      if (res.ok) {
        const data = await res.json();
        setReport({ report: data.report, generated_at: new Date().toISOString() });
        parseReportData(data.report);
        toast.success("Feasibility report generated successfully");
        setActiveTab("report");
      } else if (res.status === 402) {
        toast.error("AI Feasibility Report is a paid service (₹500). Redirecting to Services...");
        setTimeout(() => navigate("/services"), 1500);
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to generate report");
      }
    } catch (error) {
      toast.error("Failed to generate report");
    } finally {
      setGenerating(false);
    }
  };

  const calculateFSI = () => {
    if (propertyData.plot_area && propertyData.builtup_area) {
      const plotSqft = parseFloat(propertyData.plot_area) * 10.764;
      const builtup = parseFloat(propertyData.builtup_area);
      const fsi = (builtup / plotSqft).toFixed(2);
      setPropertyData({ ...propertyData, fsi_consumed: fsi });
      toast.success(`Calculated FSI: ${fsi}`);
    } else {
      toast.error("Enter Plot Area (sq.m) and Built-up Area (sq.ft) to calculate FSI");
    }
  };

  const [downloading, setDownloading] = useState(false);
  const [analyzingBlueprint, setAnalyzingBlueprint] = useState(false);
  const [blueprintResult, setBlueprintResult] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [downloadingCompliance, setDownloadingCompliance] = useState(false);

  const analyzeBlueprint = async () => {
    if (!society) return;
    setAnalyzingBlueprint(true);
    toast.info("AI Vision is analyzing the blueprint drawing. This may take 20-40 seconds...", { duration: 6000 });
    try {
      const res = await fetch(`${API}/societies/${society.society_id}/analyze-blueprint`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
        credentials: "include"
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Blueprint analysis failed");
        return;
      }
      const a = data.analysis || {};
      setBlueprintResult({ ...a, document_name: data.document_name });
      setPropertyData(prev => ({
        ...prev,
        plot_area: a.plot_area_sqm != null ? String(a.plot_area_sqm) : prev.plot_area,
        carpet_area: a.carpet_area_sqft != null ? String(a.carpet_area_sqft) : prev.carpet_area,
        builtup_area: a.builtup_area_sqft != null ? String(a.builtup_area_sqft) : prev.builtup_area
      }));
      setDocumentSources(prev => ({ ...prev, blueprint: { verified: true, data: a } }));
      toast.success(`Blueprint analyzed (${a.confidence || "unknown"} confidence). Areas auto-filled in Property Data.`);
    } catch {
      toast.error("Blueprint analysis failed");
    } finally {
      setAnalyzingBlueprint(false);
    }
  };

  const runComplianceCheck = async () => {
    if (!society) return;
    setComplianceLoading(true);
    toast.info("AI is verifying data against DCPR 2034 / UDCPR. This may take 30-60 seconds...", { duration: 6000 });
    try {
      const res = await fetch(`${API}/societies/${society.society_id}/compliance-check`, {
        method: "POST",
        credentials: "include"
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Compliance check failed");
        return;
      }
      setCompliance(data);
      toast.success("DCPR compliance report generated");
    } catch {
      toast.error("Compliance check failed");
    } finally {
      setComplianceLoading(false);
    }
  };

  const downloadCompliancePDF = async () => {
    if (!compliance?.report) return;
    setDownloadingCompliance(true);
    try {
      const res = await fetch(`${API}/reports/pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: `DCPR Compliance Report - ${society.name}`,
          subtitle: society.address || undefined,
          report_text: compliance.report,
          meta: {
            "Society": society.name,
            "Plot Area (sq.m)": propertyData.plot_area,
            "FSI Consumed": propertyData.fsi_consumed,
            "FSI Available": propertyData.fsi_available,
            "Checked On": compliance.checked_at ? new Date(compliance.checked_at).toLocaleDateString() : null
          }
        }),
        credentials: "include"
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `DCPR_Compliance_${society.name.replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch {
      toast.error("Failed to download PDF");
    } finally {
      setDownloadingCompliance(false);
    }
  };

  const downloadPDF = async () => {
    if (!report?.report) return;
    setDownloading(true);
    try {
      const res = await fetch(`${API}/reports/pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: `Feasibility Report - ${society.name}`,
          subtitle: society.address || undefined,
          report_text: report.report,
          meta: {
            "Society": society.name,
            "CTS Number": propertyData.cts_number,
            "Survey Number": propertyData.survey_number,
            "Plot Area (sq.m)": propertyData.plot_area,
            "Built-up Area (sq.ft)": propertyData.builtup_area,
            "FSI Consumed": propertyData.fsi_consumed,
            "FSI Available": propertyData.fsi_available,
            "Readiness Score": society.readiness_score != null ? `${society.readiness_score}%` : null
          }
        }),
        credentials: "include"
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Feasibility_Report_${society.name.replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch {
      toast.error("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const formatReportSections = (text) => {
    if (!text) return [];
    
    // Clean up markdown formatting (remove *, **, #, ##, etc.)
    const cleanText = text
      .replace(/\*\*([^*]+)\*\*/g, '$1')  // Remove bold **text**
      .replace(/\*([^*]+)\*/g, '$1')       // Remove italic *text*
      .replace(/`([^`]+)`/g, '$1')         // Remove inline code
      .replace(/^\s*[-*]\s*/gm, '• ')      // Convert - or * bullets to •
      .trim();
    
    const sections = [];
    const parts = cleanText.split(/(?=#{1,3}\s|(?:\d+\.\s+[A-Z]))/);
    
    parts.forEach((part, idx) => {
      if (!part.trim()) return;
      
      let title = "";
      let content = part;
      
      const headerMatch = part.match(/^(#{1,3})\s*(.+?)(?:\n|$)/);
      if (headerMatch) {
        title = headerMatch[2].trim().replace(/^#+\s*/, '');
        content = part.replace(headerMatch[0], "").trim();
      }
      
      const numberedMatch = part.match(/^(\d+)\.\s+(.+?)(?:\n|$)/);
      if (numberedMatch) {
        title = numberedMatch[2].trim();
        content = part.replace(numberedMatch[0], "").trim();
      }
      
      // Clean any remaining # from title
      title = title.replace(/^#+\s*/, '');
      
      if (title || content) {
        sections.push({ title, content, id: idx });
      }
    });
    
    return sections;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!society) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <NeedsSetup message="Set up your society to run a feasibility study." />
        </div>
      </DashboardLayout>
    );
  }

  const sections = report?.report ? formatReportSections(report.report) : [];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Feasibility Analysis
            </h1>
            <p className="text-slate-600">Detailed analysis based on property card, blueprints & documents</p>
          </div>
          <Button 
            onClick={generateReport}
            disabled={generating}
            className="bg-emerald-700 hover:bg-emerald-800"
            data-testid="generate-report-btn"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Generate Feasibility Report
              </>
            )}
          </Button>
        </div>

        {publishedFeas && (
          <Card className="border-emerald-200 bg-emerald-50/40" data-testid="published-feasibility-teaser">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-emerald-800">
                <Award className="w-5 h-5" /> Expert Feasibility Report Available
              </CardTitle>
              <CardDescription>Prepared & signed off by the MangoHouse team for your society.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="bg-white rounded-lg p-3 border border-emerald-100">
                  <p className="text-xs text-slate-500">Recommended Scheme</p>
                  <p className="text-sm font-semibold text-slate-900">{publishedFeas.recommended_scheme}</p>
                  <p className="text-xs text-slate-400">{publishedFeas.regulation}</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-emerald-100">
                  <p className="text-xs text-slate-500">Total Permissible BUA</p>
                  <p className="text-sm font-semibold text-slate-900">{publishedFeas.total_permissible_bua_sqm?.toLocaleString()} sq.m</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-emerald-100">
                  <p className="text-xs text-slate-500">Balance Saleable Carpet</p>
                  <p className="text-sm font-semibold text-slate-900">{publishedFeas.balance_saleable_carpet_sqft?.toLocaleString()} sq.ft</p>
                </div>
              </div>
              {publishedFeas.scenarios?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {publishedFeas.scenarios.map((s, i) => (
                    <span key={i} className="text-xs bg-white border border-emerald-100 rounded-full px-3 py-1">
                      @ ₹{s.sale_rate}/sqft: <span className="font-medium text-emerald-700">{s.profit_pct}% profit</span>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
                <p className="text-xs text-slate-500">
                  Signed off by {publishedFeas.professional_name} (Reg. {publishedFeas.license_number}). Indicative — requires licensed professional review.
                </p>
                {publishedFeas.vault_document_id && (
                  <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200"
                    onClick={() => window.open(`${API}/societies/${society.society_id}/documents/${publishedFeas.vault_document_id}/file`, "_blank")}
                    data-testid="view-published-feasibility-btn">
                    View Full Report
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {society && !society.paid_ai_feasibility && !isPrivileged && (
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200" data-testid="ai-feasibility-paywall">
            <CardContent className="py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  <span className="font-semibold">AI Feasibility Report is a paid service (₹500 one-time).</span>{" "}
                  Property data entry, document uploads & pre-feasibility remain free.
                </p>
              </div>
              <Button
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 flex-shrink-0"
                onClick={() => navigate("/services")}
                data-testid="unlock-ai-feasibility-btn"
              >
                Unlock for ₹500
              </Button>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="data" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Property Data
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-2">
              <FileImage className="w-4 h-4" />
              Document Sources
            </TabsTrigger>
            <TabsTrigger value="compliance" className="flex items-center gap-2" data-testid="compliance-tab">
              <ShieldCheck className="w-4 h-4" />
              DCPR Compliance
            </TabsTrigger>
            <TabsTrigger value="report" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Analysis Report
            </TabsTrigger>
          </TabsList>

          {/* Property Data Tab */}
          <TabsContent value="data" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-700" />
                  Property Card Data
                </CardTitle>
                <CardDescription>
                  Enter data from property card or extract from uploaded documents
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Plot Area */}
                  <div className="space-y-2">
                    <Label htmlFor="plot_area">Plot Area (sq.m)</Label>
                    <div className="flex gap-2">
                      <Input
                        id="plot_area"
                        type="number"
                        step="0.01"
                        value={propertyData.plot_area}
                        onChange={(e) => handlePropertyDataChange("plot_area", e.target.value)}
                        placeholder="e.g., 2500"
                      />
                      {documentSources.property_card?.verified && (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* CTS Number */}
                  <div className="space-y-2">
                    <Label htmlFor="cts_number">CTS Number</Label>
                    <Input
                      id="cts_number"
                      value={propertyData.cts_number}
                      onChange={(e) => handlePropertyDataChange("cts_number", e.target.value)}
                      placeholder="e.g., 123/A"
                    />
                  </div>

                  {/* Carpet Area */}
                  <div className="space-y-2">
                    <Label htmlFor="carpet_area">Total Carpet Area (sq.ft)</Label>
                    <Input
                      id="carpet_area"
                      type="number"
                      step="0.01"
                      value={propertyData.carpet_area}
                      onChange={(e) => handlePropertyDataChange("carpet_area", e.target.value)}
                      placeholder="e.g., 15000"
                    />
                  </div>

                  {/* Survey Number */}
                  <div className="space-y-2">
                    <Label htmlFor="survey_number">Survey Number</Label>
                    <Input
                      id="survey_number"
                      value={propertyData.survey_number}
                      onChange={(e) => handlePropertyDataChange("survey_number", e.target.value)}
                      placeholder="e.g., 45/1"
                    />
                  </div>

                  {/* Built-up Area */}
                  <div className="space-y-2">
                    <Label htmlFor="builtup_area">Total Built-up Area (sq.ft)</Label>
                    <div className="flex gap-2">
                      <Input
                        id="builtup_area"
                        type="number"
                        step="0.01"
                        value={propertyData.builtup_area}
                        onChange={(e) => handlePropertyDataChange("builtup_area", e.target.value)}
                        placeholder="e.g., 20000"
                      />
                      {documentSources.blueprint?.verified && (
                        <Badge className="bg-blue-100 text-blue-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          From Blueprint
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* FSI Consumed */}
                  <div className="space-y-2">
                    <Label htmlFor="fsi_consumed">FSI Consumed (Current)</Label>
                    <div className="flex gap-2">
                      <Input
                        id="fsi_consumed"
                        type="number"
                        step="0.01"
                        value={propertyData.fsi_consumed}
                        onChange={(e) => handlePropertyDataChange("fsi_consumed", e.target.value)}
                        placeholder="e.g., 1.33"
                      />
                      <Button 
                        variant="outline" 
                        onClick={calculateFSI}
                        title="Calculate from Plot & Built-up Area"
                      >
                        <Calculator className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* FSI Available */}
                  <div className="space-y-2">
                    <Label htmlFor="fsi_available">FSI Available (Potential)</Label>
                    <Input
                      id="fsi_available"
                      type="number"
                      step="0.01"
                      value={propertyData.fsi_available}
                      onChange={(e) => handlePropertyDataChange("fsi_available", e.target.value)}
                      placeholder="e.g., 3.0"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={fetchData}>
                    Reset
                  </Button>
                  <Button onClick={savePropertyData} className="bg-emerald-700 hover:bg-emerald-800">
                    Save Property Data
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Area Calculations Preview */}
            {propertyData.plot_area && propertyData.fsi_consumed && propertyData.fsi_available && (
              <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-emerald-700" />
                    Area Calculation Preview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="p-4 bg-white rounded-lg border">
                      <p className="text-sm text-slate-500">Current Built-up</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {(parseFloat(propertyData.plot_area) * 10.764 * parseFloat(propertyData.fsi_consumed)).toLocaleString()} sq.ft
                      </p>
                      <p className="text-xs text-slate-400">FSI {propertyData.fsi_consumed}</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg border">
                      <p className="text-sm text-slate-500">Potential Built-up</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        {(parseFloat(propertyData.plot_area) * 10.764 * parseFloat(propertyData.fsi_available)).toLocaleString()} sq.ft
                      </p>
                      <p className="text-xs text-slate-400">FSI {propertyData.fsi_available}</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg border">
                      <p className="text-sm text-slate-500">Additional Area</p>
                      <p className="text-2xl font-bold text-blue-600">
                        +{((parseFloat(propertyData.plot_area) * 10.764 * parseFloat(propertyData.fsi_available)) - 
                           (parseFloat(propertyData.plot_area) * 10.764 * parseFloat(propertyData.fsi_consumed))).toLocaleString()} sq.ft
                      </p>
                      <p className="text-xs text-slate-400">For sale + additional to members</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Document Sources Tab */}
          <TabsContent value="documents" className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              {/* Property Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="w-4 h-4 text-amber-600" />
                    Property Card (7/12)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 mb-4">
                    Extract plot area, CTS number, survey number
                  </p>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => extractFromDocument("title")}
                    disabled={extracting}
                  >
                    {extracting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ScanText className="w-4 h-4 mr-2" />
                    )}
                    Extract Data
                  </Button>
                  {documentSources.property_card?.verified && (
                    <div className="mt-3 p-2 bg-emerald-50 rounded text-xs text-emerald-700">
                      <CheckCircle className="w-3 h-3 inline mr-1" />
                      Data extracted and verified
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Blueprint/Working Drawing */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileImage className="w-4 h-4 text-blue-600" />
                    Blueprint / IOD Drawing
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 mb-4">
                    Extract built-up area from approved plans
                  </p>
                  <div className="space-y-2">
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => extractFromDocument("other")}
                      disabled={extracting || analyzingBlueprint}
                    >
                      {extracting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ScanText className="w-4 h-4 mr-2" />
                      )}
                      Extract Data (OCR)
                    </Button>
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      onClick={analyzeBlueprint}
                      disabled={extracting || analyzingBlueprint}
                      data-testid="analyze-blueprint-btn"
                    >
                      {analyzingBlueprint ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Eye className="w-4 h-4 mr-2" />
                      )}
                      AI Vision Analysis
                    </Button>
                  </div>
                  {blueprintResult && (
                    <div className="mt-3 p-2 bg-blue-50 rounded text-xs text-blue-700 space-y-1" data-testid="blueprint-result">
                      <p className="font-medium">
                        <CheckCircle className="w-3 h-3 inline mr-1" />
                        Analyzed: {blueprintResult.document_name} ({blueprintResult.confidence} confidence)
                      </p>
                      {blueprintResult.builtup_area_sqft != null && <p>Built-up: {blueprintResult.builtup_area_sqft} sq.ft</p>}
                      {blueprintResult.carpet_area_sqft != null && <p>Carpet: {blueprintResult.carpet_area_sqft} sq.ft</p>}
                      {blueprintResult.floors && <p>Floors: {blueprintResult.floors}{blueprintResult.total_units ? ` • ${blueprintResult.total_units} units` : ""}</p>}
                      {blueprintResult.observations && <p className="italic">{blueprintResult.observations}</p>}
                    </div>
                  )}
                  {!blueprintResult && documentSources.blueprint?.verified && (
                    <div className="mt-3 p-2 bg-blue-50 rounded text-xs text-blue-700">
                      <CheckCircle className="w-3 h-3 inline mr-1" />
                      Data extracted and verified
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Municipal Corporation Documents */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-purple-600" />
                    Municipal Records
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 mb-4">
                    CC, OC, or municipal approval documents
                  </p>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => extractFromDocument("noc")}
                    disabled={extracting}
                  >
                    {extracting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ScanText className="w-4 h-4 mr-2" />
                    )}
                    Extract Data
                  </Button>
                  {documentSources.municipal_drawing?.verified && (
                    <div className="mt-3 p-2 bg-purple-50 rounded text-xs text-purple-700">
                      <CheckCircle className="w-3 h-3 inline mr-1" />
                      Data extracted and verified
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="py-4">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Document Upload Required</p>
                    <p className="text-sm text-amber-700 mt-1">
                      To extract data, first upload the relevant documents in the <strong>Documents</strong> section. 
                      Supported: Property card (7/12 extract), IOD/CC drawings, Municipal approval documents.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* DCPR Compliance Tab */}
          <TabsContent value="compliance" className="space-y-4">
            {!compliance?.report ? (
              <Card className="border-dashed border-2">
                <CardContent className="py-16 text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-6">
                    <ShieldCheck className="w-10 h-10 text-blue-700" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                    DCPR 2034 / UDCPR Compliance Check
                  </h3>
                  <p className="text-slate-600 max-w-md mx-auto mb-6">
                    AI verifies your property data against Mumbai DCPR 2034 and Maharashtra UDCPR regulations — FSI limits, area consistency, and redevelopment eligibility.
                  </p>
                  <Button 
                    onClick={runComplianceCheck}
                    disabled={complianceLoading}
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="run-compliance-btn"
                  >
                    {complianceLoading ? (
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    ) : (
                      <ShieldCheck className="w-5 h-5 mr-2" />
                    )}
                    Run Compliance Check
                  </Button>
                  <p className="text-xs text-slate-400 mt-4">Requires plot area saved in Property Data</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-blue-700" />
                        {compliance.regulation === "UDCPR 2020" ? "UDCPR" : "DCPR"} Compliance Report
                      </CardTitle>
                      <CardDescription>
                        Verified against {compliance.regulation || "DCPR 2034 & UDCPR"} regulations
                        {compliance.zone ? ` • ${compliance.zone}` : ""}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {compliance.checked_at && (
                        <Badge variant="outline" className="text-xs">
                          <Clock className="w-3 h-3 mr-1" />
                          {new Date(compliance.checked_at).toLocaleDateString()}
                        </Badge>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={runComplianceCheck}
                        disabled={complianceLoading}
                        data-testid="rerun-compliance-btn"
                      >
                        {complianceLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-2" />
                        )}
                        Re-check
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={downloadCompliancePDF}
                        disabled={downloadingCompliance}
                        data-testid="download-compliance-pdf-btn"
                      >
                        {downloadingCompliance ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        PDF
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2" data-testid="compliance-report">
                    {compliance.report.split('\n').map((line, i) => {
                      const t = line.trim();
                      if (!t) return null;
                      if (t.includes('[PASS]')) {
                        return (
                          <div key={i} className="flex items-start gap-2 p-2 bg-emerald-50 rounded text-sm text-emerald-800">
                            <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" />
                            <span>{t.replace('[PASS]', '').trim()}</span>
                          </div>
                        );
                      }
                      if (t.includes('[REVIEW]') || t.includes('[MISSING DATA]')) {
                        return (
                          <div key={i} className="flex items-start gap-2 p-2 bg-amber-50 rounded text-sm text-amber-800">
                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                            <span>{t.replace('[REVIEW]', '').replace('[MISSING DATA]', 'Missing data:').trim()}</span>
                          </div>
                        );
                      }
                      if (/^\d+\.\s+[A-Z]/.test(t) || (t.length < 60 && t.endsWith(':'))) {
                        return (
                          <h3 key={i} className="text-base font-semibold text-slate-900 mt-4" style={{ fontFamily: 'Manrope' }}>
                            {t.replace(/:$/, '')}
                          </h3>
                        );
                      }
                      if (/^[-•]\s+/.test(t)) {
                        return (
                          <div key={i} className="flex items-start gap-2 text-sm text-slate-700 ml-1">
                            <span className="text-blue-600 mt-0.5">•</span>
                            <span>{t.replace(/^[-•]\s+/, '')}</span>
                          </div>
                        );
                      }
                      return <p key={i} className="text-sm text-slate-700 leading-relaxed">{t}</p>;
                    })}
                  </div>
                  <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-100">
                    <div className="flex gap-3">
                      <Sparkles className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      <p className="text-xs text-amber-700">
                        AI-generated verification based on DCPR 2034 / UDCPR guidelines. For official gazette verification, consult MCGM building proposal department and a licensed architect.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Report Tab */}
          <TabsContent value="report" className="space-y-4">
            {!report?.report ? (
              <Card className="border-dashed border-2">
                <CardContent className="py-16 text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-full flex items-center justify-center mx-auto mb-6">
                    <TrendingUp className="w-10 h-10 text-emerald-700" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                    Generate Feasibility Report
                  </h3>
                  <p className="text-slate-600 max-w-md mx-auto mb-6">
                    Enter property data and upload documents, then generate a detailed feasibility analysis.
                  </p>
                  <Button 
                    onClick={generateReport}
                    disabled={generating}
                    size="lg"
                    className="bg-emerald-700 hover:bg-emerald-800"
                  >
                    {generating ? (
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-5 h-5 mr-2" />
                    )}
                    Generate Feasibility Report
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Key Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-100">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-slate-600">Viability</span>
                        <ArrowUpRight className="w-4 h-4 text-emerald-600" />
                      </div>
                      <p className={`text-2xl font-bold ${parsedData?.viability === 'High' ? 'text-emerald-600' : parsedData?.viability === 'Low' ? 'text-red-600' : 'text-amber-600'}`}>
                        {parsedData?.viability || 'Analyzing'}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-slate-600">FSI Potential</span>
                        <BarChart3 className="w-4 h-4 text-blue-600" />
                      </div>
                      <p className="text-2xl font-bold text-blue-600">
                        {parsedData?.fsiPotential || propertyData.fsi_available || '3.0'}x
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-slate-600">Timeline</span>
                        <Clock className="w-4 h-4 text-purple-600" />
                      </div>
                      <p className="text-2xl font-bold text-purple-600">
                        {parsedData?.timeline || '36-48'}
                      </p>
                      <p className="text-xs text-slate-500">months</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-100">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-slate-600">Risk Level</span>
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                      </div>
                      <p className="text-2xl font-bold text-amber-600">
                        {parsedData?.riskLevel || 'Medium'}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Readiness Meter */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChart className="w-5 h-5 text-emerald-700" />
                      Readiness Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-8">
                      <div className="flex flex-col items-center">
                        <div className="relative w-48 h-48">
                          <svg className="w-full h-full transform -rotate-90">
                            <circle cx="96" cy="96" r="88" fill="none" stroke="#e2e8f0" strokeWidth="12" />
                            <circle
                              cx="96" cy="96" r="88" fill="none" stroke="#059669" strokeWidth="12"
                              strokeDasharray={`${(society.readiness_score / 100) * 553} 553`}
                              strokeLinecap="round"
                            />
                          </svg>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-4xl font-bold text-slate-900">{society.readiness_score}%</span>
                            <span className="text-sm text-slate-500">Ready</span>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <h4 className="font-semibold text-slate-900">Data Sources</h4>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                            <span className="text-sm">Property Card</span>
                            {documentSources.property_card?.verified ? (
                              <Badge className="bg-emerald-100 text-emerald-700">Verified</Badge>
                            ) : (
                              <Badge variant="outline">Pending</Badge>
                            )}
                          </div>
                          <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                            <span className="text-sm">Blueprint/IOD</span>
                            {documentSources.blueprint?.verified ? (
                              <Badge className="bg-emerald-100 text-emerald-700">Verified</Badge>
                            ) : (
                              <Badge variant="outline">Pending</Badge>
                            )}
                          </div>
                          <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                            <span className="text-sm">Municipal Records</span>
                            {documentSources.municipal_drawing?.verified ? (
                              <Badge className="bg-emerald-100 text-emerald-700">Verified</Badge>
                            ) : (
                              <Badge variant="outline">Pending</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Detailed Report */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-emerald-700" />
                          Detailed Feasibility Report
                        </CardTitle>
                        <CardDescription>Based on property card, documents, and area calculations</CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        {report.generated_at && (
                          <Badge variant="outline" className="text-xs">
                            <Clock className="w-3 h-3 mr-1" />
                            {new Date(report.generated_at).toLocaleDateString()}
                          </Badge>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={downloadPDF}
                          disabled={downloading}
                          data-testid="download-feasibility-pdf-btn"
                        >
                          {downloading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4 mr-2" />
                          )}
                          Download PDF
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-slate max-w-none">
                      {sections.length > 0 ? (
                        <div className="space-y-6">
                          {sections.map((section) => (
                            <div key={section.id} className="border-l-4 border-emerald-200 pl-4">
                              {section.title && (
                                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                                  {section.title}
                                </h3>
                              )}
                              <div className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                                {section.content.split('\n').map((line, i) => {
                                  if (line.startsWith('-')) {
                                    return (
                                      <div key={i} className="flex items-start gap-2 my-1">
                                        <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                                        <span>{line.replace(/^-\s*/, '')}</span>
                                      </div>
                                    );
                                  }
                                  return line && <p key={i} className="mb-2">{line}</p>;
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {report.report}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Disclaimer */}
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="py-4">
                    <div className="flex gap-3">
                      <Sparkles className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-800">Professional Advisory Note</p>
                        <p className="text-sm text-amber-700 mt-1">
                          This feasibility report is generated using AI analysis based on provided data and Mumbai DCPR 2034 guidelines. 
                          For accurate legal and financial assessments, verify all measurements with original documents and consult 
                          certified architects, lawyers, and chartered accountants specializing in redevelopment.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};
